/**
 * AppRuntime — pure TypeScript runtime for Cogo v2 apps.
 *
 * Zero React imports. All state management, flow execution, event dispatch,
 * page tree building, and navigation logic lives here.
 * React shell (AppShell) subscribes to state changes and re-renders.
 *
 * Block-Centric Lifecycle:
 * - Runtime is created ONCE per app (not per blocks change).
 * - Blocks are added incrementally via BlockLifecycleManager.
 * - Init flows execute in 2 phases: layout (await) → content (parallel).
 * - Generation counter protects against StrictMode double-mount.
 *
 * Orchestration delegates to AppRuntimeCore (shared with Flutter).
 * This wrapper keeps rendering + React glue: registry / buildPageTree /
 * normalizeNavLayout / streaming / WebMCP tool installation.
 */

import {
  ElementRegistry,
  registerAllRenderers,
  normalizeGeneratedLayout,
  normalizeNavLayout,
} from "@cogo/react-renderer";
import type {
  GeneratedLayoutUiNode,
  NavLayoutUiNode,
} from "@cogo/react-renderer";
import type {
  App,
  BlockDef,
  UiElement,
  EventRef,
  Flow,
  FlowMap,
  Page,
  NavigationData,
  DataSource,
} from "@cogo/types-v2";
import { isAbsolutePosition, parseBindingKey } from "@cogo/types-v2";
import {
  StateManager,
  ActionFlowEngine,
  BlockLifecycleManager,
  DataSourceManager,
  createAppRuntimeCore,
  resolveGovernanceConfig,
} from "@cogo/runtime-v2";
import type {
  AppRuntimeCore,
  AppRuntimeCoreRef,
  PlatformAdapter,
  ToastHandler,
  SandboxAwarePlatform,
  EventExecutionReceipt,
} from "@cogo/runtime-v2";
import type { SandboxModules } from "../sandbox/index.js";
import { EventDispatcherV2 } from "./event-dispatcher";
import type { HostServices } from "../host/host-services";
import type { MotionRootLike } from "../host/motion-registry";
import { cropImageWeb } from "../host/crop-image-web";
import {
  createWebMCPIntegration,
  type WebMCPIntegration,
  type JsonSchema,
  generateToolsFromApp,
  generatePageContextTools,
  generateVisibleElementsTools,
  formatToolError,
} from "@cogo/webmcp-runtime";

// Phase 152: Sandbox-aware platform adapter extension.
// (The SandboxAwarePlatform type is re-used from @cogo/runtime-v2 but extended with
// Partial<SandboxModules> at the web layer — the Core's sandbox interface is a structural
// subset so we just cast via unknown when constructing Core options.)
interface LocalSandboxAwarePlatform extends PlatformAdapter {
  sandbox?: Partial<SandboxModules>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────

/**
 * Walk each block's ui root and apply nav-layout normalization. Mutates in
 * place — callers pass the blocks record before lifecycle.addBlocks so the
 * state manager / event bindings see the rewritten tree.
 *
 * No-op on blocks missing a ui root or non-object ui trees. Compliant
 * classNames are unchanged; only violations are rewritten.
 */
function applyNavLayoutNormalization(blocks: Record<string, BlockDef>): void {
  for (const block of Object.values(blocks)) {
    const root = block?.ui?.root;
    if (root && typeof root === "object") {
      normalizeNavLayout(root as unknown as NavLayoutUiNode);
      normalizeGeneratedLayout(root as unknown as GeneratedLayoutUiNode);
    }
  }
}

// ─── Public interface ─────────────────────────────────────────────────────

export interface AppRuntime {
  stateManager: StateManager;
  actionEngine: ActionFlowEngine;
  eventDispatcher: EventDispatcherV2;
  lifecycle: BlockLifecycleManager;
  registry: ElementRegistry;
  currentRoute: string;
  activeBlocks: Set<string>;

  navigate(route: string): void;
  syncExternalRoute(route: string): Promise<void>;
  resolveSymbol(expr: string): unknown;
  dispatchEvent(elementId: string, eventType: string, payload?: unknown): void;
  getCurrentPage(): Page;
  getBlockRenderingExpectation(): {
    renderableBlockKeys: string[];
    exclusions: Record<string, string>;
  };
  buildPageTree(): UiElement[];
  subscribe(listener: () => void): () => void;
  runInitFlows(options?: { isRouteChange?: boolean }): Promise<void>;
  /** Fire all active `trigger:"tick"` flows once with `{ event: { dt } }` — driven by the
   *  AppV2 requestAnimationFrame clock. The runtime half of the `tick` trigger. */
  runTickFlows(dt: number): void;
  /** @deprecated Use addBlocks() instead */
  updateBlocks(blocks: Record<string, BlockDef>): void;
  addBlocks(blocks: Record<string, BlockDef>): string[];
  registerAllFlows(): void;
  addStreamingChunk(chunk: string | Record<string, any>): void;
  clearStreaming(): void;
  nextGeneration(): number;
  destroy(): void;
}

export interface ThemeTokens {
  colors?: Record<string, string>;
  /**
   * Pass-through CSS custom properties — the app's OWN variable names, with
   * their `--` prefix, emitted verbatim by `getIsolatedAppStyle()`:
   * `{ '--fg': '#2A2623', '--gold': '#C9A227' }`.
   *
   * For names the renderer's colour-slot vocabulary does not know. Converted
   * applications reference their design system through arbitrary Tailwind
   * values (`bg-[var(--fg)]`), which resolve against these names and nothing
   * else. No meaning is assigned and no mapping is performed — and therefore
   * no theme switching: one value serves both colour schemes. A name that is
   * a real slot belongs in `colors`, not here.
   */
  cssVars?: Record<string, string>;
  spacing?: Record<string, string | number>;
  typography?: Record<
    string,
    {
      size?: string | number;
      weight?: string | number;
      lineHeight?: string | number;
      family?: string;
    }
  >;
  radius?: Record<string, string | number> | string;
  shadow?: Record<string, string>;
  /**
   * Font stacks (Phase 393) — emitted as --font-sans / --font-mono /
   * --font-heading CSS variables when passed through getIsolatedAppStyle().
   * Parity with v1 CogoBlock ThemeTokens.
   */
  fonts?: { sans?: string; mono?: string; heading?: string };
  /** @deprecated Use `fonts.sans` instead. Kept for backward compat with legacy v1 callers. */
  fontFamily?: string;
  fontWeightDefault?: number;
}

export interface AppRuntimeOptions {
  platform?: PlatformAdapter;
  onToast?: ToastHandler;
  initialRoute?: string;
  onNavigate?: (route: string) => void | Promise<void>;
  onEventExecution?: (receipt: EventExecutionReceipt) => void;
  designSystem?: Record<string, string>;
  /** Theme tokens (colors + radius) — consumed by app wrapper/portal scoping, not host :root */
  themeTokens?: ThemeTokens;
  /**
   * Host services backing the four host-effect actions (emitEvent / openModal /
   * invokeCallback / mediaControl). When provided, the runtime wires the engine's
   * four hooks to these registries (block-scoped ones resolve the dispatching
   * block automatically). Omit for headless/no-op behavior (back-compat).
   */
  hostServices?: HostServices;
  /** Resolve this AppV2 instance's render root for DOM-scoped motion lookup. */
  getMotionRoot?: () => MotionRootLike | null;
  /** Direct hooks — assigned only when `hostServices` is absent (advanced/manual wiring). */
  onEmitEvent?: (name: string, payload?: unknown) => void;
  onOpenModal?: (modalId: string, props: Record<string, unknown>) => void;
  onInvokeCallback?: (name: string, args?: unknown) => void;
  onMediaControl?: (command: string, args: Record<string, unknown>) => void;
  onRunMotion?: (params: Record<string, unknown>) => unknown | Promise<unknown>;
  /**
   * Optional element-renderer registry (workstream D — render registry
   * composition). When omitted, the runtime uses the process-wide
   * `getSharedRegistry()` (all renderers — the Studio/dev default, unchanged).
   * A host that wants deploy-selective rendering passes
   * `composeElementRegistry(manifest.meta.renderProfile.lanes)` here.
   */
  registry?: ElementRegistry;
}

// ─── Singleton registry ───────────────────────────────────────────────────

let sharedRegistry: ElementRegistry | null = null;
export function getSharedRegistry(): ElementRegistry {
  if (!sharedRegistry) {
    sharedRegistry = new ElementRegistry();
    registerAllRenderers(sharedRegistry);
  }
  return sharedRegistry;
}

// ─── Factory ──────────────────────────────────────────────────────────────

export function createAppRuntime(
  manifest: App,
  blocks: Record<string, BlockDef>,
  options: AppRuntimeOptions = {},
): AppRuntime {
  return new AppRuntimeImpl(manifest, blocks, options);
}

// ─── Implementation ───────────────────────────────────────────────────────

class AppRuntimeImpl implements AppRuntime {
  // ── Rendering-only fields (NOT in Core) ──────────────────────────────────
  readonly registry: ElementRegistry;

  private readonly streamingNodes = new Map<string, any>();
  private readonly streamingParentToChildren = new Map<string, string[]>();
  private readonly streamingRoots = new Set<string>();

  // ── Core — owns all orchestration ────────────────────────────────────────
  private readonly core: AppRuntimeCore;

  // ── webmcp — installed by the web wrapper at runInitFlows() ──────────────
  private webmcp: WebMCPIntegration | null = null;

  // ── Cached manifest reference for buildPageTree ───────────────────────────
  private readonly manifest: App;

  // ── Back-reference for web-specific actions (csvExport / jsonExport) ──────
  private readonly options: AppRuntimeOptions;

  constructor(
    manifest: App,
    blocks: Record<string, BlockDef>,
    options: AppRuntimeOptions,
  ) {
    this.manifest = manifest;
    this.options = options;
    // Workstream D: honor an injected (lane-composed) registry; default to the
    // shared all-renderer singleton (back-compatible — unchanged when omitted).
    this.registry = options.registry ?? getSharedRegistry();

    // ── Build hostHooks bundle from options ────────────────────────────────
    // Core's hostHooks are called with (blockNs, name, args) for block-scoped hooks.
    // wireHostHooks expects a getActiveBlockId() supplier — we translate it here.
    let hostHooks:
      | {
          onEmitEvent?: (name: string, payload?: unknown) => void;
          onOpenModal?: (
            modalId: string,
            props: Record<string, unknown>,
          ) => void;
          onInvokeCallback?: (
            blockNs: string | null,
            name: string,
            args?: unknown,
          ) => void;
          onMediaControl?: (
            blockNs: string | null,
            command: string,
            args: Record<string, unknown>,
          ) => void;
          onRunMotion?: (
            blockNs: string | null,
            params: Record<string, unknown>,
          ) => unknown | Promise<unknown>;
        }
      | undefined;

    if (options.hostServices) {
      const {
        eventBus,
        modalStore,
        callbackRegistry,
        mediaRegistry,
        motionRegistry,
      } = options.hostServices;
      hostHooks = {
        onEmitEvent: (name, payload) => eventBus.emit(name, payload),
        onOpenModal: (modalId, props) => modalStore.open(modalId, props),
        // Core passes blockNs as first arg — forward to block-scoped registries
        onInvokeCallback: (blockNs, name, args) =>
          callbackRegistry.invoke(blockNs, name, args),
        onMediaControl: (blockNs, command, args) =>
          mediaRegistry.control(blockNs, command, args),
        onRunMotion: (blockNs, params) =>
          motionRegistry.run(blockNs, params, options.getMotionRoot?.()),
      };
    } else {
      if (
        options.onEmitEvent ||
        options.onOpenModal ||
        options.onInvokeCallback ||
        options.onMediaControl ||
        options.onRunMotion
      ) {
        hostHooks = {
          onEmitEvent: options.onEmitEvent,
          onOpenModal: options.onOpenModal,
          // Adapt: Core passes (blockNs, name, args); direct hook expects (name, args)
          onInvokeCallback: options.onInvokeCallback
            ? (_blockNs, name, args) => options.onInvokeCallback!(name, args)
            : undefined,
          onMediaControl: options.onMediaControl
            ? (_blockNs, command, args) =>
                options.onMediaControl!(command, args)
            : undefined,
          onRunMotion: options.onRunMotion
            ? (_blockNs, params) => options.onRunMotion!(params)
            : undefined,
        };
      }
    }

    // ── Normalize nav layout before adding blocks to Core ─────────────────
    applyNavLayoutNormalization(blocks);

    // ── Build Core ─────────────────────────────────────────────────────────
    const self = this;
    this.core = createAppRuntimeCore(manifest, blocks, {
      platform: options.platform,
      onToast: options.onToast,
      initialRoute: options.initialRoute,
      onNavigate: options.onNavigate,
      onEventExecution: options.onEventExecution,
      designSystem: options.designSystem,
      // Step 4 Phase 4.3 — write-governance canary: COGO_RUNTIME_GOVERNANCE=audit|enforce opts this
      // web mount in (default off → undefined → Core's zero-overhead default). Contract comes from
      // manifest.meta.stateContract (Phase 4.2).
      governance: resolveGovernanceConfig(),
      hostHooks,
      // cropImageWeb is the web implementation of cropImage (CSS/DOM).
      // Falls back to basePlatform.cropImage when present (sandbox mode).
      cropImage: (imageId: string, ratio?: string) => {
        const base = options.platform as LocalSandboxAwarePlatform | undefined;
        if (base?.cropImage) return base.cropImage(imageId, ratio);
        return cropImageWeb(imageId, ratio);
      },
      // sandboxPlatform: pass the platform as-is; Core handles sandbox.*
      sandboxPlatform: options.platform as SandboxAwarePlatform | undefined,
      // WebMCP installer — installed at each runInitFlows() (survives StrictMode).
      webmcp: {
        install: (ref: AppRuntimeCoreRef) => {
          if (typeof window === "undefined") return { dispose: () => {} };
          self.webmcp?.dispose();
          const integration = createWebMCPIntegration({
            installPolyfill: true,
            targetWindow: (
              options.platform as LocalSandboxAwarePlatform | undefined
            )?.sandbox?.events?.sandboxWindow,
          });
          self.webmcp = integration;
          self._doInstallWebMCPTools(integration, ref);
          return {
            dispose: () => {
              integration.dispose();
              self.webmcp = null;
            },
          };
        },
      },
    });

  }

  // ─── Getters: expose Core's orchestration fields ──────────────────────────

  get stateManager(): StateManager {
    return this.core.stateManager;
  }

  get actionEngine(): ActionFlowEngine {
    return this.core.actionEngine;
  }

  /**
   * eventDispatcher — expose Core's dispatcher.
   * Tests call .getBinding() and .addBinding() on this.
   * CoreEventDispatcher is structurally compatible with EventDispatcherV2
   * (same public API). We cast through unknown to satisfy the interface type.
   */
  get eventDispatcher(): EventDispatcherV2 {
    return this.core.eventDispatcher as unknown as EventDispatcherV2;
  }

  get lifecycle(): BlockLifecycleManager {
    return this.core.lifecycle;
  }

  get currentRoute(): string {
    return this.core.currentRoute;
  }

  /**
   * activeBlocks — expose Core's private activeBlocks Set for tests that
   * access `(runtime as any).activeBlocks`. The Core owns this state, so we
   * proxy to it via the private field.
   */
  get activeBlocks(): Set<string> {
    return (this.core as any).activeBlocks as Set<string>;
  }

  // ─── Public API — delegate to Core ────────────────────────────────────────

  navigate(route: string): void {
    this.core.navigate(route);
  }

  syncExternalRoute(route: string): Promise<void> {
    return this.core.syncExternalRoute(route);
  }

  resolveSymbol(expr: string): unknown {
    return this.core.resolveSymbol(expr);
  }

  dispatchEvent(elementId: string, eventType: string, payload?: unknown): void {
    this.core.dispatchEvent(elementId, eventType, payload);
  }

  getCurrentPage(): Page {
    return this.core.getCurrentPage();
  }

  /**
   * Return the block boundaries that the canonical stack renderer must emit
   * for the current runtime state. Publication proof compares this independent
   * runtime expectation with the actual DOM boundary set.
   */
  getBlockRenderingExpectation(): {
    renderableBlockKeys: string[];
    exclusions: Record<string, string>;
  } {
    const page = this.getCurrentPage();
    if (
      (page.layout && page.layout !== "stack") ||
      this.streamingNodes.size > 0
    ) {
      return {
        renderableBlockKeys: [],
        exclusions: Object.fromEntries(
          [...(page.blocks ?? [])]
            .sort()
            .map((blockName) => [blockName, "unsupported-layout"]),
        ),
      };
    }
    const blocks = this.lifecycle.getAllBlocks();
    const renderableBlockKeys: string[] = [];
    const exclusions: Record<string, string> = {};
    for (const blockName of page.blocks ?? []) {
      const block = blocks[blockName];
      const root = block?.ui?.root;
      if (!block) {
        exclusions[blockName] = "missing-block";
        continue;
      }
      if (block.meta?.type === "logic") {
        exclusions[blockName] = "logic";
        continue;
      }
      if (!root) {
        exclusions[blockName] = "missing-root";
        continue;
      }
      if (isStubBlock(root, blockName)) {
        exclusions[blockName] = "stub";
        continue;
      }

      const rawRoot = root as UiElement & Record<string, unknown>;
      const options = rawRoot.options as Record<string, unknown> | undefined;
      const visibility =
        rawRoot.visibleWhen ??
        rawRoot.visible ??
        options?.visibleWhen ??
        options?.visible;
      if (visibility !== undefined) {
        const resolved = this.resolveSymbol(String(visibility));
        if (
          resolved === false ||
          resolved === "false" ||
          resolved == null ||
          resolved === 0 ||
          resolved === "" ||
          (Array.isArray(resolved) && resolved.length === 0)
        ) {
          exclusions[blockName] = "visibility";
          continue;
        }
      }

      const iterateOver = rawRoot.iterateOver ?? options?.iterateOver;
      if (
        iterateOver !== undefined &&
        root.type !== "block-ref" &&
        root.type !== "carousel"
      ) {
        const items = this.resolveSymbol(String(iterateOver));
        if (!Array.isArray(items) || items.length === 0) {
          exclusions[blockName] = "empty-iteration";
          continue;
        }
      }
      renderableBlockKeys.push(blockName);
    }
    return {
      renderableBlockKeys: renderableBlockKeys.sort(),
      exclusions: Object.fromEntries(
        Object.entries(exclusions).sort(([left], [right]) =>
          left.localeCompare(right),
        ),
      ),
    };
  }

  subscribe(listener: () => void): () => void {
    return this.core.subscribe(listener);
  }

  runInitFlows(options?: { isRouteChange?: boolean }): Promise<void> {
    return this.core.runInitFlows(options);
  }

  runTickFlows(dt: number): void {
    this.core.runTickFlows(dt);
  }

  addBlocks(blocks: Record<string, BlockDef>): string[] {
    applyNavLayoutNormalization(blocks);
    // Add blocks via lifecycle (state + flows + bindings); Core.addBlocks internally
    // calls its own runInitFlows — but tests spy on the PUBLIC wrapper's runInitFlows.
    // We therefore replicate the page-block guard here and call THIS.runInitFlows so
    // spies on the wrapper method intercept it (behaviorally identical).
    const added = this.core.lifecycle.addBlocks(blocks);
    if (added.length > 0) {
      // Refresh activeBlocks in Core (private method — access via any)
      (this.core as any).refreshActiveBlocks?.();
      const page = this.core.getCurrentPage();
      if (page) {
        const globalBlocks = (this.manifest as any).globalBlocks ?? [];
        const pageBlocks = new Set([...(page.blocks ?? []), ...globalBlocks]);
        const kebabToCamel = (s: string) =>
          s.replace(/-([a-z])/g, (_: string, c: string) => c.toUpperCase());
        const camelToKebab = (s: string) =>
          s.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
        const hasNewPageBlock = added.some(
          (name) =>
            pageBlocks.has(name) ||
            pageBlocks.has(kebabToCamel(name)) ||
            pageBlocks.has(camelToKebab(name)),
        );
        if (hasNewPageBlock) {
          this.runInitFlows({ isRouteChange: false });
        }
      }
    }
    return added;
  }

  /** @deprecated Use addBlocks() instead — kept for backward compatibility */
  updateBlocks(blocks: Record<string, BlockDef>): void {
    this.core.updateBlocks(blocks);
  }

  registerAllFlows(): void {
    this.core.registerAllFlows();
    console.log(
      `[AppRuntime] Registered flows from ${Object.keys(this.lifecycle.getAllBlocks()).length} blocks`,
    );
  }

  nextGeneration(): number {
    return this.core.nextGeneration();
  }

  destroy(): void {
    this.core.destroy();
    this.webmcp = null;
  }

  // ─── Streaming (rendering-only) ────────────────────────────────────────────

  addStreamingChunk(chunk: string | Record<string, any>): void {
    if (typeof chunk === "string") {
      const lines = chunk.split("\n");
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        try {
          const node = JSON.parse(trimmed);
          this.ingestStreamingNode(node);
        } catch (e) {
          console.warn(
            "[AppRuntime] Failed to parse streaming JSONL line:",
            trimmed,
            e,
          );
        }
      }
    } else if (chunk && typeof chunk === "object") {
      this.ingestStreamingNode(chunk);
    }
  }

  clearStreaming(): void {
    this.streamingNodes.clear();
    this.streamingParentToChildren.clear();
    this.streamingRoots.clear();
    // notify listeners via core
    (this.core as any).notifyListeners?.();
  }

  private ingestStreamingNode(node: any): void {
    if (!node || !node.id) return;

    this.streamingNodes.set(node.id, node);

    const parentId = node.parentId || node.parent_id;
    if (parentId) {
      let children = this.streamingParentToChildren.get(parentId);
      if (!children) {
        children = [];
        this.streamingParentToChildren.set(parentId, children);
      }
      if (!children.includes(node.id)) {
        children.push(node.id);
      }

      if (this.streamingNodes.has(parentId)) {
        this.streamingRoots.delete(node.id);
      } else {
        this.streamingRoots.add(node.id);
      }
    } else {
      this.streamingRoots.add(node.id);
    }

    const myChildren = this.streamingParentToChildren.get(node.id) || [];
    for (const childId of myChildren) {
      this.streamingRoots.delete(childId);
    }

    (this.core as any).notifyListeners?.();
  }

  private buildStreamingPageTree(): UiElement[] {
    const visited = new Set<string>();

    const buildSubtree = (nodeId: string): UiElement | null => {
      if (visited.has(nodeId)) return null;
      visited.add(nodeId);

      const node = this.streamingNodes.get(nodeId);
      if (!node) return null;

      const { parentId, parent_id, children: rawChildren, ...rest } = node;

      const uiNode: UiElement = {
        id: node.id,
        type: node.type || "container",
        className: node.className || node.class || "",
        ...rest,
        children: [],
      };

      const childIds = this.streamingParentToChildren.get(nodeId) || [];
      if (childIds.length > 0) {
        uiNode.children = childIds
          .map((id) => buildSubtree(id))
          .filter(Boolean) as UiElement[];
      }

      return uiNode;
    };

    const roots = Array.from(this.streamingRoots)
      .map((id) => buildSubtree(id))
      .filter(Boolean) as UiElement[];

    return roots;
  }

  // ─── Page tree building (rendering-only) ──────────────────────────────────

  buildPageTree(): UiElement[] {
    if (this.streamingNodes.size > 0) {
      return this.buildStreamingPageTree();
    }

    const page = this.getCurrentPage();
    if (!page) return [];
    // Ensure page.blocks is an array (may be undefined during generation)
    if (!Array.isArray(page.blocks)) page.blocks = [];

    // ── Sidebar layout: page.layout === "sidebar" ────────────────
    if (page.layout === "sidebar") {
      return this.buildSidebarPageTree(page);
    }

    // ── Absolute layout: blocks positioned by x,y,w,h ────────────
    if (page.layout === "absolute") {
      return this.buildAbsolutePageTree(page);
    }

    // ── Custom layout: page.ui.root defines the tree ──────────────
    if ((page as any).ui?.root) {
      return [(page as any).ui.root];
    }

    const blocks = this.lifecycle.getAllBlocks();

    // ── Layout-block wrapping: page.layout references a block name ──
    const layoutBlockName =
      page.layout !== "stack" && page.layout ? page.layout : undefined;
    const layoutBlock = layoutBlockName ? blocks[layoutBlockName] : undefined;

    if (layoutBlock?.ui?.root) {
      const enrichedLayout = this.enrichBlock(layoutBlockName!);
      const layoutUi = structuredClone(enrichedLayout?.ui ?? layoutBlock.ui);
      const contentSlot = findContentSlot(layoutUi.root!);
      if (contentSlot) {
        const cls = contentSlot.className ?? "";
        if (!cls.includes("flex-col")) {
          contentSlot.className = cls + (cls ? " " : "") + "flex flex-col";
        }

        contentSlot.children = page.blocks
          .filter((name) => {
            if (name === layoutBlockName) return false;
            const block = blocks[name];
            return block && block.meta?.type !== "logic";
          })
          .map((name) => {
            const root = this.enrichBlock(name)?.ui?.root;
            if (!root) return null;
            const bl = page.blockLayout?.[name];
            const gapClass = bl?.gap ?? bl?.spacing ?? "";
            if (gapClass) {
              return tagSectionBoundary(
                {
                  id: `section-${name}`,
                  type: "container",
                  className: gapClass,
                  children: [root],
                } as UiElement,
                name,
              );
            }
            return root;
          })
          .filter(Boolean) as UiElement[];
      }
      return [layoutUi.root!];
    }

    // ── Stack layout (default) ───────────────────────────────────
    const blockElements: UiElement[] = [];
    for (const blockName of page.blocks) {
      const block = blocks[blockName];
      if (!block || block.meta?.type === "logic") continue;
      const enriched = this.enrichBlock(blockName);
      const root = enriched?.ui?.root;
      if (!root) continue;

      // Skip stub blocks — blocks with only a single text child containing [block-name] placeholder
      if (isStubBlock(root, blockName)) continue;

      const bl = page.blockLayout?.[blockName];

      if (isHeaderBlock(root)) {
        blockElements.push(root);
        continue;
      }

      const widthClass = bl?.width ? (WIDTH_CLASS_MAP[bl.width] ?? "") : "";
      const gapClass = bl?.gap ?? bl?.spacing ?? "";
      const isFooter = isFooterBlock(blockName, root);
      const wrapperParts = ["overflow-x-hidden w-full", widthClass, gapClass];
      if (isFooter) wrapperParts.push("mt-auto");
      const wrapperClass = wrapperParts.filter(Boolean).join(" ");

      blockElements.push(
        tagSectionBoundary(
          {
            id: `section-${blockName}`,
            type: "container",
            tag: isFooter ? "footer" : "section",
            className: wrapperClass,
            children: [root],
          },
          blockName,
        ),
      );
    }

    return [
      {
        id: `page-${page.id}`,
        type: "container",
        className: "overflow-x-hidden max-w-[100vw] w-full flex flex-col",
        children: blockElements,
      },
    ];
  }

  /**
   * Build sidebar layout tree.
   */
  private buildSidebarPageTree(page: Page): UiElement[] {
    const blocks = this.lifecycle.getAllBlocks();
    const sidebarElements: UiElement[] = [];
    const headerElements: UiElement[] = [];
    const contentElements: UiElement[] = [];

    for (const blockName of page.blocks ?? []) {
      const block = blocks[blockName];
      if (!block || block.meta?.type === "logic") continue;
      const enriched = this.enrichBlock(blockName);
      const root = enriched?.ui?.root;
      if (!root) continue;

      const isSidebar =
        root.type === "aside" ||
        root.tag === "aside" ||
        root.variant === "sidebar";
      const isHeader =
        root.variant === "header" ||
        root.tag === "header" ||
        root.type === "nav" ||
        blockName.includes("topnav") ||
        root.position?.type === "sticky";

      if (isSidebar) {
        sidebarElements.push(normalizeSidebarRoot(root));
      } else if (isHeader) {
        headerElements.push(root);
      } else {
        const bl = page.blockLayout?.[blockName];
        const gapClass = bl?.gap ?? bl?.spacing ?? "";
        if (gapClass) {
          contentElements.push(
            tagSectionBoundary(
              {
                id: `section-${blockName}`,
                type: "container",
                className: gapClass,
                children: [root],
              } as UiElement,
              blockName,
            ),
          );
        } else {
          contentElements.push(root);
        }
      }
    }

    const mainArea: UiElement = {
      id: `page-${page.id}-main`,
      type: "container",
      className: "flex flex-1 flex-col overflow-hidden",
      children: [
        ...headerElements,
        {
          id: `page-${page.id}-content`,
          type: "container",
          tag: "main" as string,
          className: "flex-1 overflow-y-auto p-6",
          children: contentElements,
        } as UiElement,
      ],
    };

    const sidebarWrapper: UiElement = {
      id: `page-${page.id}-sidebar`,
      type: "container",
      tag: "aside" as string,
      className:
        "hidden w-64 shrink-0 overflow-y-auto border-r border-border md:block",
      children: sidebarElements,
    };

    return [
      {
        id: `page-${page.id}`,
        type: "container",
        className: "flex h-screen overflow-hidden",
        children: [
          ...(sidebarElements.length > 0 ? [sidebarWrapper] : []),
          mainArea,
        ],
      },
    ];
  }

  /**
   * Build absolute-positioned page tree.
   * Each block is wrapped in a position:absolute container using page.blockPositions.
   * Canvas size from page.canvas (defaults to 1920x1080).
   */
  private buildAbsolutePageTree(page: Page): UiElement[] {
    const blocks = this.lifecycle.getAllBlocks();
    const canvasW = page.canvas?.w ?? 1920;
    const canvasH = page.canvas?.h ?? 1080;
    const positions = page.blockPositions ?? {};
    const blockNames = page.blocks?.length
      ? page.blocks.filter((name) => name in positions)
      : Object.keys(positions);

    const blockElements: UiElement[] = [];

    for (let i = 0; i < blockNames.length; i++) {
      const name = blockNames[i];
      const pos = positions[name];
      if (!pos || !isAbsolutePosition(pos)) continue;

      const enriched = this.enrichBlock(name);
      const root = enriched?.ui?.root;
      if (!root) continue;

      blockElements.push(
        tagSectionBoundary(
          {
            id: `section-${name}`,
            type: "container",
            style: {
              position: "absolute",
              left: pos.x,
              top: pos.y,
              width: pos.w,
              height: pos.h,
              overflow: "hidden",
              zIndex: i,
            },
            children: [root],
          } as UiElement,
          name,
        ),
      );
    }

    return [
      {
        id: `page-${page.id}`,
        type: "container",
        style: {
          position: "relative",
          width: canvasW,
          height: canvasH,
        },
        children: blockElements,
      } as UiElement,
    ];
  }

  // ─── WebMCP (rendering-only) ───────────────────────────────────────────────

  /**
   * Install WebMCP tool registrations on the integration instance.
   * Called by the webmcp.install hook in Core's runInitFlows().
   * Idempotent: Core disposes the previous instance before calling install.
   */
  private _doInstallWebMCPTools(
    integration: WebMCPIntegration,
    ref: AppRuntimeCoreRef,
  ): void {
    // Connect runtime → polyfill (state, events, flows)
    integration.onRuntimeReady({
      stateManager: ref.stateManager,
      actionEngine: ref.actionEngine,
      eventDispatcher: {
        dispatch: (id: string, type: string, payload: unknown) =>
          this.core.dispatchEvent(id, type, payload),
      },
    });

    const reg = integration.registry;

    // ── navigate_to ──
    // Build route→nav params lookup from layout block event bindings.
    const pages = this.manifest.pages ?? [];
    const routes = pages.map((p) => p.route);
    const routeDesc = pages
      .map((p) => `"${p.route}" — ${p.title ?? p.id}`)
      .join(", ");

    const routeToNavParams = new Map<string, Record<string, unknown>>();
    const blocks = this.lifecycle.getAllBlocks();
    for (const [, block] of Object.entries(blocks)) {
      const bindings = (block.events as Record<string, unknown> | undefined)
        ?.bindings as
        | Record<string, { flow?: string; params?: Record<string, unknown> }>
        | undefined;
      if (!bindings) continue;
      for (const binding of Object.values(bindings)) {
        if (binding.flow === "navigateTo" && binding.params?.route) {
          routeToNavParams.set(binding.params.route as string, binding.params);
        }
      }
    }

    reg.register({
      name: "navigate_to",
      description: `Navigate to a page. Routes: ${routeDesc}`,
      inputSchema: {
        type: "object",
        properties: { route: { type: "string", enum: routes } },
        required: ["route"],
      },
      handler: async (params) => {
        const route = params.route as string;
        try {
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("page_load_timeout")), 5000),
          );

          // Navigate first (this triggers runInitFlows + syncNavigation)
          const navResult = this.core.navigate(route, { force: true });
          const settledNav =
            navResult instanceof Promise ? navResult : Promise.resolve();
          const navigationReceipt = await Promise.race([
            settledNav,
            timeoutPromise,
          ]);
          if (
            !navigationReceipt ||
            navigationReceipt.status !== "completed" ||
            navigationReceipt.executed !== true
          ) {
            return {
              ok: false,
              error: navigationReceipt?.error ?? "navigation_not_executed",
              navigationReceipt,
            };
          }

          // After navigation, apply sidebar state from event bindings.
          const freshNavParams = new Map<string, Record<string, unknown>>();
          const freshBlocks = this.lifecycle.getAllBlocks();
          for (const [, block] of Object.entries(freshBlocks)) {
            const bindings = (
              block.events as Record<string, unknown> | undefined
            )?.bindings as
              | Record<
                  string,
                  { flow?: string; params?: Record<string, unknown> }
                >
              | undefined;
            if (!bindings) continue;
            for (const binding of Object.values(bindings)) {
              if (binding.flow === "navigateTo" && binding.params?.route) {
                freshNavParams.set(
                  binding.params.route as string,
                  binding.params,
                );
              }
            }
          }
          const navParams = freshNavParams.get(route);
          console.log(
            `[navigate_to] route="${route}" freshNavParams.size=${freshNavParams.size} match=${!!navParams}`,
            navParams,
          );
          if (navParams) {
            if (navParams.section)
              this.stateManager.setState(
                "activeSection",
                navParams.section as string,
              );
            if (navParams.label)
              this.stateManager.setState(
                "activeNavItem",
                navParams.label as string,
              );
            if (navParams.sectionLabel)
              this.stateManager.setState(
                "sectionLabel",
                navParams.sectionLabel as string,
              );
          }

          const page = this.getCurrentPage();
          return {
            ok: true,
            route,
            currentRoute: this.core.currentRoute,
            currentPageId: page?.id ?? null,
            loadedBlocks: navigationReceipt.loadedBlocks,
            navigationReceipt: {
              status: navigationReceipt.status,
              executed: navigationReceipt.executed,
            },
          };
        } catch (err) {
          if (err instanceof Error && err.message === "page_load_timeout") {
            return { ok: false, error: "page_load_timeout" };
          }
          throw err;
        }
      },
    });

    // ── execute_flow ──
    const flowNames = ref.actionEngine.getRegisteredFlowNames();
    reg.register({
      name: "execute_flow",
      description: `Execute a named ActionFlow. Available: ${flowNames.slice(0, 20).join(", ")}${flowNames.length > 20 ? "..." : ""}`,
      inputSchema: {
        type: "object",
        properties: {
          flowName: { type: "string", description: "Flow name to execute" },
          params: {
            type: "object",
            description: "Parameters to pass to the flow",
          },
        },
        required: ["flowName"],
      },
      handler: async (params) => {
        const flow = ref.actionEngine.findFlow(params.flowName as string);
        if (!flow) throw new Error(`Flow "${params.flowName}" not found`);

        const stateChanges: string[] = [];
        const originalSetState = ref.stateManager.setState.bind(
          ref.stateManager,
        );
        ref.stateManager.setState = (path: string, value: unknown) => {
          stateChanges.push(path);
          originalSetState(path, value);
        };

        try {
          await ref.actionEngine.executeFlow(
            flow,
            (params.params as Record<string, unknown>) ?? {},
          );
          return {
            ok: true,
            result: {
              flowName: params.flowName,
              stateChanges: [...new Set(stateChanges)],
              lastAction:
                (flow as any).steps?.[(flow as any).steps?.length - 1]
                  ?.action ?? null,
            },
          };
        } catch (err) {
          const formatted = formatToolError(err, "execute_flow");
          return {
            ok: false,
            error: {
              message: formatted.message,
              code: formatted.classification,
              hint: "Check flow name and parameters",
            },
          };
        } finally {
          ref.stateManager.setState = originalSetState;
        }
      },
    });

    // ── get_state ──
    reg.register({
      name: "get_state",
      description:
        "Read app state at a given path (e.g. 'appData.kpiData' or 'overviewKpi.revenue')",
      inputSchema: {
        type: "object",
        properties: { path: { type: "string" } },
        required: ["path"],
      },
      handler: async (params) => {
        const val = ref.stateManager.get(params.path as string);
        return val;
      },
      metadata: { readOnly: true },
    });

    // ── set_state ──
    reg.register({
      name: "set_state",
      description: "Write a value to app state at a given path",
      inputSchema: {
        type: "object",
        properties: {
          path: { type: "string" },
          value: {
            type: "string",
            description: "JSON-encoded value or plain string",
          },
        },
        required: ["path", "value"],
      },
      handler: async (params) => {
        let val: unknown = params.value;
        try {
          val = JSON.parse(params.value as string);
        } catch {
          /* use as-is */
        }
        ref.stateManager.setState(params.path as string, val);
        return { ok: true, path: params.path };
      },
    });

    // ── get_available_flows ──
    reg.register({
      name: "get_available_flows",
      description:
        "List all executable flows with their step counts and action types",
      inputSchema: { type: "object", properties: {} },
      handler: async () => {
        const flowNames = ref.actionEngine.getRegisteredFlowNames();
        return flowNames.map((name) => {
          const flow = ref.actionEngine.findFlow(name);
          const steps = (flow as any)?.steps ?? [];
          return {
            name,
            stepCount: steps.length,
            actions: steps.map((s: any) => s.action).filter(Boolean),
          };
        });
      },
      metadata: { readOnly: true },
    });

    // ── dispatch_event ──
    reg.register({
      name: "dispatch_event",
      description: "Dispatch a UI event (e.g. tap) on an element by ID",
      inputSchema: {
        type: "object",
        properties: {
          elementId: { type: "string" },
          eventType: {
            type: "string",
            description: "Event type: tap, change, etc.",
          },
        },
        required: ["elementId", "eventType"],
      },
      handler: async (params) => {
        this.core.dispatchEvent(
          params.elementId as string,
          params.eventType as string,
        );
        return { ok: true };
      },
    });

    // ── DSL-generated tools ──
    const blockMap: Record<string, any> = {};
    const flowMap: Record<string, Record<string, any>> = {};
    const allBlocks = this.lifecycle.getAllBlocks();
    for (const page of this.manifest.pages ?? []) {
      for (const blockName of page.blocks ?? []) {
        const block = allBlocks[blockName];
        if (block?.ui) blockMap[blockName] = block.ui;
        if (block?.flow) {
          const raw = block.flow as Record<string, unknown>;
          flowMap[blockName] = (raw?.flows as Record<string, any>) ?? raw ?? {};
        }
      }
    }
    try {
      const dslTools = generateToolsFromApp(
        this.manifest as any,
        blockMap,
        flowMap,
      );
      for (const tool of dslTools) {
        if (reg.getTool(tool.name)) continue;
        const originalHandler = tool.handler;
        reg.register({
          ...tool,
          handler: async (params) => {
            const runtimeRef = {
              stateManager: ref.stateManager,
              actionEngine: {
                executeFlow: async (
                  flowName: string,
                  context: Record<string, unknown> = {},
                ) => {
                  const blockName = tool.metadata?.blockName;
                  const flow =
                    (blockName
                      ? ref.actionEngine.findFlow(`${blockName}.${flowName}`)
                      : undefined) ?? ref.actionEngine.findFlow(flowName);
                  if (!flow) {
                    throw new Error(
                      `WebMCP ActionFlow not found: ${blockName ? `${blockName}.` : ""}${flowName}`,
                    );
                  }
                  return ref.actionEngine.executeFlow(flow, {
                    event: context,
                  });
                },
              },
            };
            const result = originalHandler(params);
            if (typeof result === "function")
              return (result as Function)(runtimeRef);
            return result;
          },
        });
      }
    } catch (e) {
      console.warn("[WebMCP] DSL tool generation failed:", e);
    }

    // ── Block-level meta.webmcp tools ──
    const activeBlockNames = new Set<string>();
    for (const page of this.manifest.pages ?? []) {
      for (const bn of page.blocks ?? []) activeBlockNames.add(bn);
    }
    for (const blockName of activeBlockNames) {
      const block = allBlocks[blockName];
      const webmcpConfig = block?.meta?.webmcp;
      if (!webmcpConfig?.expose) continue;
      for (const [toolName, toolDef] of Object.entries(
        webmcpConfig.tools ?? {},
      )) {
        if (reg.getTool(toolName)) continue;
        const flowName = toolDef.flow ?? toolName;
        reg.register({
          name: toolName,
          description: toolDef.description,
          inputSchema: (toolDef.inputSchema ?? {
            type: "object",
            properties: {},
          }) as unknown as JsonSchema,
          handler: async (params) => {
            const flow =
              ref.actionEngine.findFlow(`${blockName}.${flowName}`) ??
              ref.actionEngine.findFlow(flowName);
            if (flow)
              return ref.actionEngine.executeFlow(flow, { event: params });
            return { error: `Flow ${flowName} not found` };
          },
          metadata: {
            blockName,
            readOnly: toolDef.readOnly ?? false,
            tags: toolDef.tags ?? [],
          },
        });
      }
    }

    // ── Page context tools ──
    const pageContextRuntime = {
      getApplication: () => ({
        id: this.manifest.appId,
        name: this.manifest.name,
        pageCount: this.manifest.pages?.length ?? 0,
      }),
      getCurrentPage: () => {
        const page = this.getCurrentPage();
        if (!page) return null;
        return {
          id: page.id,
          route: page.route,
          title: page.title,
          blocks: page.blocks,
        };
      },
      blocks: allBlocks as Record<string, { ui?: { root?: any } }>,
      getAuthState: () => {
        try {
          const isAuth = ref.stateManager.get("appData.auth.isAuthenticated");
          const user = ref.stateManager.get("appData.auth.user") as
            | Record<string, unknown>
            | undefined;
          return {
            isAuthenticated: !!isAuth,
            userName: user?.name as string | undefined,
            userType: user?.userType as string | undefined,
          };
        } catch {
          return { isAuthenticated: false };
        }
      },
    };
    const contextTools = [
      ...generatePageContextTools(pageContextRuntime),
      ...generateVisibleElementsTools(pageContextRuntime),
    ];
    for (const tool of contextTools) {
      reg.register(tool);
    }

    console.log(
      `[WebMCP] Installed ${reg.getManifest().length} tools for "${this.manifest.name}"`,
    );
  }

  // ─── Private: block enrichment ─────────────────────────────────────────────

  /** Enrich block with events.json bindings injected into UI tree. */
  private enrichBlock(blockName: string): BlockDef | undefined {
    const blocks = this.lifecycle.getAllBlocks();
    const block = blocks[blockName];
    if (!block) return undefined;
    const enriched = injectEventsIntoUi(block);
    const ui = structuredClone(enriched.ui);
    const root = ui?.root as
      | (UiElement & { __cogoRuntimeBlockBoundary?: string })
      | undefined;
    if (!root) return { ...enriched, ui } as BlockDef;
    walkTree(root, (element) => {
      delete (element as UiElement & { __cogoRuntimeBlockBoundary?: string })
        .__cogoRuntimeBlockBoundary;
    });
    root.__cogoRuntimeBlockBoundary = blockName;
    return {
      ...enriched,
      ui,
    } as BlockDef;
  }
}

// ─── Pure utility functions (no class, no React) ──────────────────────────

const kebabToCamel = (s: string) =>
  s.replace(/-([a-z])/g, (_: string, c: string) => c.toUpperCase());
const camelToKebab = (s: string) =>
  s.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();

/**
 * Tag a `section-<name>` wrapper with its block-boundary marker.
 *
 * `enrichBlock` marks the block's own root with `__cogoRuntimeBlockBoundary`, but
 * buildPageTree wraps that root in a `section-<name>` container. The renderer emits
 * `data-cogo-block-boundary`/`data-cogo-block-id` from this marker, and the A3.4 IDE
 * block-selection contract resolves the owning block from the OUTERMOST per-block
 * element via `target.closest('[data-cogo-block-id]')` (build-mode-canvas
 * handleElementClick) — which must also cover clicks that land on the wrapper's own
 * gap/padding region, not just descendants. Tagging the wrapper here makes that region
 * addressable. The block root keeps its own marker so root-scoped identity (e.g.
 * co-active blocks sharing an `id: "root"`) still resolves; `.closest()` finds the
 * nearest boundary in both cases.
 */
function tagSectionBoundary(wrapper: UiElement, blockName: string): UiElement {
  (
    wrapper as UiElement & { __cogoRuntimeBlockBoundary?: string }
  ).__cogoRuntimeBlockBoundary = blockName;
  return wrapper;
}

/** Map blockLayout.width semantic values to Tailwind classes. */
const WIDTH_CLASS_MAP: Record<string, string> = {
  constrained: "max-w-[72rem] mx-auto",
  narrow: "max-w-[48rem] mx-auto",
  full: "",
};

/** A page-level sidebar owns positioning; nested generated off-canvas tokens do not. */
function normalizeSidebarRoot(root: UiElement): UiElement {
  const next = structuredClone(root);
  const tokens = (next.className ?? "").split(/\s+/).filter(Boolean);
  const conflicting = new Set([
    "fixed",
    "absolute",
    "left-0",
    "right-0",
    "top-0",
    "bottom-0",
    "h-screen",
    "min-h-screen",
    "-translate-x-full",
    "translate-x-full",
    "lg:translate-x-0",
    "md:translate-x-0",
    "sm:translate-x-0",
  ]);
  const cleaned = tokens.filter(
    (token) =>
      !conflicting.has(token) &&
      !/^z-/.test(token) &&
      !/^(?:sm|md|lg|xl):z-/.test(token),
  );
  for (const required of ["h-full", "w-64", "translate-x-0"]) {
    if (!cleaned.includes(required)) cleaned.push(required);
  }
  next.className = cleaned.join(" ");
  return next;
}

/** Detect header/sticky blocks — render without section wrapper. */
function isHeaderBlock(root: UiElement | undefined): boolean {
  if (!root) return false;
  if (root.variant === "header" || root.tag === "header") return true;
  if (root.position?.type === "sticky") return true;
  const opts = (root as unknown as Record<string, unknown>).options as
    | Record<string, unknown>
    | undefined;
  if (opts?.position === "sticky") return true;
  return false;
}

/** Detect footer blocks by name or tag. */
function isFooterBlock(
  blockName: string,
  root: UiElement | undefined,
): boolean {
  if (blockName.includes("footer")) return true;
  if (root?.tag === "footer" || root?.variant === "footer") return true;
  return false;
}

/**
 * Detect stub blocks — blocks whose only content is a [block-name] placeholder text.
 */
function isStubBlock(root: UiElement | undefined, blockName: string): boolean {
  if (!root) return true;
  const children = root.children;
  if (!children || children.length !== 1) return false;
  const child = children[0];
  if (child.type !== "text") return false;
  const text = (child.text ?? "").trim();
  return text === `[${blockName}]` || /^\[[\w-]+\]$/.test(text);
}

export function matchRoute(pattern: string, path: string): boolean {
  const patternParts = pattern.split("/");
  const pathParts = path.split("/");
  if (patternParts.length !== pathParts.length) return false;
  return patternParts.every((part, i) => {
    if (part.startsWith(":")) return true;
    return part === pathParts[i];
  });
}

/** Return -1 if pattern does not match path. Otherwise return a specificity score. */
export function scorePageSpecificity(pattern: string, path: string): number {
  const patternParts = pattern.split("/");
  const pathParts = path.split("/");
  if (patternParts.length !== pathParts.length) return -1;
  let score = 0;
  for (let i = 0; i < patternParts.length; i++) {
    const part = patternParts[i];
    if (part.startsWith(":")) continue;
    if (part !== pathParts[i]) return -1;
    score++;
  }
  return score;
}

function extractRouteParams(
  pattern: string,
  path: string,
): Record<string, string> {
  const params: Record<string, string> = {};
  const patternParts = pattern.split("/");
  const pathParts = path.split("/");
  patternParts.forEach((part, i) => {
    if (part.startsWith(":") && pathParts[i]) {
      params[part.slice(1)] = pathParts[i];
    }
  });
  return params;
}

function walkElements(
  element: UiElement | undefined | null,
  fn: (el: UiElement) => void,
): void {
  if (!element) return;
  fn(element);
  element.children?.forEach((child) => walkElements(child, fn));
}

function resolveVarDefault(varDef: unknown): unknown {
  if (varDef != null && typeof varDef === "object" && !Array.isArray(varDef)) {
    const def = varDef as Record<string, unknown>;
    if (def.default !== undefined) return structuredClone(def.default);
    if (def.type === undefined) return structuredClone(varDef);
    return undefined;
  }
  return varDef !== undefined ? structuredClone(varDef) : undefined;
}

function resolveBindingParams(
  params: Record<string, unknown>,
  context: Record<string, unknown>,
  stateManager?: { resolve(expr: string): unknown },
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === "string") {
      const pureMatch = /^#\{([^}]+)\}$/.exec(value);
      if (pureMatch) {
        const contextVal = evaluateContextExpression(
          pureMatch[1].trim(),
          context,
        );
        if (contextVal !== undefined) {
          result[key] = contextVal;
        } else if (stateManager) {
          const stateVal = stateManager.resolve(value);
          result[key] = stateVal !== value ? stateVal : undefined;
        } else {
          result[key] = undefined;
        }
      } else {
        result[key] = value;
      }
    } else {
      result[key] = value;
    }
  }
  return result;
}

function evaluateContextExpression(
  expr: string,
  context: Record<string, unknown>,
): unknown {
  const pathValue = getFromContext(expr, context);
  if (pathValue !== undefined) return pathValue;

  let processed = expr;
  const refs: Array<{ match: string; value: unknown }> = [];
  processed = expr.replace(/([a-zA-Z_][\w]*(?:\.[\w]+)*)/g, (match) => {
    const val = getFromContext(match, context);
    if (val !== undefined) {
      refs.push({ match, value: val });
      if (typeof val === "number") return val.toString();
      if (typeof val === "string") {
        const n = parseFloat(val);
        return isNaN(n) ? JSON.stringify(val) : n.toString();
      }
      return String(val);
    }
    return match;
  });

  if (refs.length === 0) return undefined;
  if (refs.length === 1 && expr.trim() === refs[0].match) return refs[0].value;

  const trimmed = processed.trim();
  const numResult = parseFloat(trimmed);
  if (!isNaN(numResult) && /^-?\d+(\.\d+)?$/.test(trimmed)) return numResult;

  const binMatch = /^(-?\d+(?:\.\d+)?)\s*([+\-*/])\s*(-?\d+(?:\.\d+)?)$/.exec(
    trimmed,
  );
  if (binMatch) {
    const l = parseFloat(binMatch[1]);
    const r = parseFloat(binMatch[3]);
    switch (binMatch[2]) {
      case "+":
        return l + r;
      case "-":
        return l - r;
      case "*":
        return l * r;
      case "/":
        return r !== 0 ? l / r : 0;
    }
  }

  return refs[0]?.value;
}

function getFromContext(path: string, ctx: Record<string, unknown>): unknown {
  const keys = path.split(".");
  let current: unknown = ctx;
  for (const key of keys) {
    if (current == null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

/** Find the content slot in a layout UI tree. */
export function findContentSlot(root: UiElement): UiElement | null {
  let found: UiElement | null = null;
  walkTree(root, (el) => {
    if (el.id === "content-body") found = el;
  });
  if (found) return found;

  walkTree(root, (el) => {
    if (!found && el.id?.includes("content")) found = el;
  });
  if (found) return found;

  let deepest: UiElement | null = null;
  let maxDepth = 0;
  function findDeepest(el: UiElement, depth: number) {
    if (el.type === "container" && (!el.children || el.children.length === 0)) {
      if (depth > maxDepth) {
        maxDepth = depth;
        deepest = el;
      }
    }
    el.children?.forEach((c) => findDeepest(c, depth + 1));
  }
  findDeepest(root, 0);
  return deepest;
}

function walkTree(el: UiElement, fn: (el: UiElement) => void) {
  fn(el);
  el.children?.forEach((c) => walkTree(c, fn));
}

/**
 * Inject events.json bindings into UI tree elements.
 * Renderers check element.events.tap to attach onClick handlers.
 */
function injectEventsIntoUi(block: BlockDef): BlockDef {
  const evts = block.events as Record<string, unknown> | undefined;
  if (!evts) return block;

  const bindings = (evts.bindings ?? evts) as Record<string, unknown>;
  if (!bindings || typeof bindings !== "object") return block;

  const eventMap = new Map<string, Record<string, unknown>>();
  for (const [key, binding] of Object.entries(bindings)) {
    // [#802 e8c2307cf port] shared canonical binding-key parse — behavior-identical to the prior
    // inline lastIndexOf(":") (skips when colonIdx<=0), kept in sync via @cogo/types-v2.
    const parsed = parseBindingKey(key);
    if (!parsed) continue;
    const { elementId, eventType } = parsed;
    if (!eventMap.has(elementId)) eventMap.set(elementId, {});
    eventMap.get(elementId)![eventType] = binding;
  }

  if (eventMap.size === 0) return block;

  const ui = structuredClone(block.ui);
  function walk(node: Record<string, unknown> | undefined): void {
    if (!node) return;
    const id = node.id as string | undefined;
    if (id && eventMap.has(id)) {
      const existing = (node.events ?? {}) as Record<string, unknown>;
      node.events = { ...existing, ...eventMap.get(id) };
    }
    const children = node.children as
      | Array<Record<string, unknown>>
      | undefined;
    if (Array.isArray(children)) children.forEach(walk);
    const template = node.template as Record<string, unknown> | undefined;
    if (template) walk(template);
  }
  walk(ui?.root as unknown as Record<string, unknown>);

  return { ...block, ui } as BlockDef;
}
